<script lang="ts">
  import type { CalendarDay, CalendarSwim } from "../lib/swimCalendar";
  import { fmtMeters } from "../lib/format";

  interface Props {
    day: CalendarDay;
    onPick: (uuid: string) => void;
    onClose: () => void;
  }
  let { day, onPick, onClose }: Props = $props();

  function timeOf(swim: CalendarSwim): string {
    return new Date(swim.startDate).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") onClose();
  }
</script>

<svelte:window onkeydown={onKey} />

<div
  class="dp-scrim"
  role="presentation"
  onclick={onClose}
  onkeydown={(e) => (e.key === "Enter" || e.key === " ") && onClose()}
></div>

<div class="dp-panel" role="dialog" aria-modal="true" aria-label="Choose a swim">
  <span class="dp-grabber" aria-hidden="true"></span>
  <p class="dp-title">{day.swims.length} swims · {day.date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
  {#each day.swims as swim (swim.uuid)}
    <button
      type="button"
      class="dp-row"
      data-testid="day-picker-row"
      aria-label={`${fmtMeters(swim.distanceMeters)}${swim.isOpenWater ? " open water" : ""} at ${timeOf(swim)}`}
      onclick={() => onPick(swim.uuid)}
    >
      <span class="dp-dist">{fmtMeters(swim.distanceMeters)}</span>
      {#if swim.isOpenWater}
        <span class="dp-ow"><span class="dp-ow-dot" aria-hidden="true"></span>Open water</span>
      {/if}
      <span class="dp-time">{timeOf(swim)}</span>
      <span class="dp-chevron" aria-hidden="true">›</span>
    </button>
  {/each}
  <button type="button" class="dp-close" onclick={onClose}>Close</button>
</div>

<style>
  .dp-scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.72);
    z-index: 60;
    touch-action: none;
  }
  .dp-panel {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 61;
    background: rgba(22, 23, 27, 0.97);
    backdrop-filter: blur(24px) saturate(1.2);
    -webkit-backdrop-filter: blur(24px) saturate(1.2);
    border-top-left-radius: var(--radius-card);
    border-top-right-radius: var(--radius-card);
    padding: var(--space-3) var(--space-4) calc(var(--space-6) + env(safe-area-inset-bottom));
    box-shadow: 0 -8px 28px rgba(0, 0, 0, 0.55);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .dp-grabber {
    width: 36px;
    height: 4px;
    border-radius: var(--radius-pill);
    background: var(--text-dim);
    align-self: center;
    margin-bottom: var(--space-2);
  }
  .dp-title {
    margin: 0 0 var(--space-1);
    font-size: 15px;
    color: var(--text-muted);
  }
  .dp-row {
    min-height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--space-3);
    border: 0;
    border-radius: var(--radius-button);
    background: var(--bg-raised);
    cursor: pointer;
    font-family: var(--font-text);
  }
  .dp-row:active {
    background: rgba(183, 199, 232, 0.12);
  }
  .dp-dist {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
  }
  /* Open-water marker on a swim row — dot + label, held left next to distance;
     the space-between layout keeps time + chevron on the right. */
  .dp-ow {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-right: auto;
    padding-left: var(--space-3);
    font-size: 14px;
    color: var(--text-muted);
  }
  .dp-ow-dot {
    width: 7px;
    height: 7px;
    border-radius: var(--radius-pill);
    background: var(--accent-secondary);
  }
  .dp-time {
    font-size: 16px;
    color: var(--text-body);
  }
  .dp-chevron {
    color: var(--text-muted);
    font-size: 20px;
    line-height: 1;
    margin-left: var(--space-2);
  }
  .dp-close {
    min-height: 44px;
    margin-top: var(--space-2);
    border: 0;
    background: transparent;
    color: var(--text-muted);
    font-size: 16px;
    font-family: var(--font-text);
    cursor: pointer;
  }
</style>
